// @ts-nocheck
import React, { useEffect, useState } from 'react';
import * as Authservices from '../Services/services';
import { selectableAccounts } from '../utils/accounts';

/**
 * Active-account picker for callers whose email maps to more than one Salesforce account (ACOP-79).
 *
 * Renders NOTHING when there is one account or fewer, which is almost everyone — a picker with a
 * single option is noise, and the account is selected silently server-side in that case.
 *
 * Selecting writes through to the session cookie, which is what makes the choice sticky across
 * navigation and reloads. The page is then reloaded rather than being patched in place: every
 * section holds account-scoped data it fetched earlier, so re-fetching from a clean slate is the
 * honest way to switch — a partial refresh would leave stale rows from the previous account on
 * screen, which is exactly the confusion this feature exists to remove.
 */
function AccountSwitcher({ collapsed, accountCtx }) {
    const [selectedId, setSelectedId] = useState('');
    const [busy, setBusy] = useState(false);

    // Which accounts are genuinely selectable — pure, unit-tested logic in utils/accounts.
    // Kept out of this component so the "never show a picker to a single-account user" rule can be
    // asserted directly (utils/accounts.test.ts) rather than only observed by rendering.
    const accounts = React.useMemo(() => selectableAccounts(accountCtx), [accountCtx?.accounts]);

    // The session payload arrives asynchronously on load, so track it rather than seeding once.
    useEffect(() => {
        setSelectedId(accountCtx?.selectedAccountId || '');
    }, [accountCtx?.selectedAccountId]);

    // One account (or none, or an unusable payload) => no control at all, in either nav state.
    if (accounts.length < 2) return null;

    const onChange = async (e) => {
        const accountId = e.target.value;
        if (!accountId || accountId === selectedId) return;

        setBusy(true);
        try {
            const response = await Authservices.select_account({ accountId });
            if (response?.data?.status !== 1) {
                setBusy(false);
                return;
            }
            // The session cookie now carries the selection, and validate-login replays it into the
            // app on the next load — so a plain reload is all that is needed to re-establish it.
            window.location.reload();
        } catch {
            setBusy(false);
        }
    };

    const selectedName =
        accounts.find((a) => a.id === selectedId)?.name || 'Select account';

    if (collapsed) {
        // Collapsed rail has no room for a control; show the active account's initial so the user
        // can still tell which account they are in.
        return (
            <div
                className="copilot-sidenav-account copilot-sidenav-account--collapsed text-center"
                title={selectedName}
            >
                <span className="badge bg-secondary">{(selectedName || '?').charAt(0)}</span>
            </div>
        );
    }

    return (
        <div className="copilot-sidenav-account">
            {/* Same class as the LEARNING / SUPPORT headings so the label matches them exactly —
                it previously used Bootstrap's `small`, which is a different size and weight and
                carries none of the letter-spacing, so it read as a foreign element in the rail. */}
            <label
                className="copilot-sidenav-section-heading text-uppercase text-muted d-block"
                htmlFor="copilot-account-switcher"
            >
                Account
            </label>
            <select
                id="copilot-account-switcher"
                className="form-select form-select-sm rounded-3"
                value={selectedId}
                onChange={onChange}
                disabled={busy}
                aria-label="Active account"
            >
                {accounts.map((a) => (
                    <option key={a.id} value={a.id}>
                        {a.name}
                    </option>
                ))}
            </select>
        </div>
    );
}

export default AccountSwitcher;
